/**
 * Asking the panel on Slack: the channel roster, the message preview, and the send.
 *
 * Its own module because taActionBoardApi.ts sits at the repo's 500-line
 * ceiling — the same reason the final-round calls moved to taFinalRoundApi.ts.
 * Everything here is re-exported from taActionBoardApi, so existing callers are
 * unaffected by the split.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { tracked } from './taActionBoardAnalytics';
import type { AskDestination, GradedSlot } from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

export interface SlackChannel {
  id: string;
  name: string;
}

/**
 * Public Slack channels for the destination typeahead. Cached module-side for
 * the session — the proxy caches the Slack roster for 10 minutes anyway.
 * An empty list (missing scope, Slack error) degrades to free-text entry.
 */
let channelsPromise: Promise<SlackChannelList> | null = null;
export function fetchSlackChannels(): Promise<SlackChannelList> {
  channelsPromise ??= authenticatedFetch(`${BASE}/slack-channels`)
    .then((r) => r.json())
    .then((d) => ({
      channels: Array.isArray(d.channels) ? (d.channels as SlackChannel[]) : [],
      // Carried through, not swallowed: the roster needs the bot's channels:read
      // scope, and without it the picker is simply empty — which reads as "we
      // have no channels" rather than "the app cannot list them" (Theresa, Sep 2026).
      error: typeof d.error === 'string' ? d.error : null,
    }))
    .catch(() => {
      channelsPromise = null; // allow a retry on the next modal open
      return { channels: [], error: 'Could not load the channel list — type the #channel-name.' };
    });
  return channelsPromise;
}

/** The channel roster plus why it may be empty. */
export interface SlackChannelList {
  channels: SlackChannel[];
  error: string | null;
}

export async function previewAskPanelists(payload: {
  postingId: string;
  slots: GradedSlot[];
  candidateCount: number;
  /**
   * Overrides the language derived from the role's tags.
   *
   * The tags describe the job AD; this message goes to the hiring team, and
   * which language that team works in varies by department. Omit it and the
   * server decides, exactly as before.
   */
  language?: 'de' | 'en';
}): Promise<AskPreview> {
  const res = await authenticatedFetch(`${BASE}/ask-panelists/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<AskPreview>;
}

/**
 * The previewed message plus what could NOT go in it.
 *
 * The server re-grades the panel's calendars when it builds this, so a ticked
 * slot can legitimately be free by now or gone from the window. Those used to
 * be filtered out silently, leaving the recruiter to approve a message with
 * fewer options than they picked (reported Sep 2026).
 */
/** One panelist's own DM: their name, their items, nothing anyone else has to do. */
export interface PanelistAsk {
  email: string;
  /** How many of THEIR meetings would have to move across the proposal */
  moves: number;
  message: string;
}

export interface AskPreview {
  /** The joint text — what a channel post says. */
  message: string;
  /**
   * One per DM recipient. Panelists with nothing to move are absent: they get
   * no DM, which is the whole point of differentiating.
   */
  panelists?: PanelistAsk[];
  /** Which destination fits this proposal — the modal defaults to it. */
  suggestedDestination?: 'dms' | 'channel';
  /** The whole panel, so the modal can name who is being left out. */
  panel?: string[];
  slotCount: number;
  /** Ticked starts that re-graded to free — nothing to ask, hold them instead. */
  skippedFree?: string[];
  /** Ticked starts the fresh grading does not contain at all. */
  unknown?: string[];
  /** Ticked starts past the per-message cap. */
  overCap?: string[];
  maxSlots?: number;
  /** The language this preview is in — the modal's toggle opens on it. */
  language?: 'de' | 'en';
}

export async function askPanelists(payload: {
  postingId: string;
  opportunityIds: string[];
  slots: GradedSlot[];
  candidateCount: number;
  /** DM mode: the recruiter's edited text per recipient, keyed by email. */
  messages?: Record<string, string>;
  roleTitle: string;
  /** DMs to the panelists (default) or ONE message to a Slack channel */
  destination?: AskDestination;
  /** The recruiter's edited text. Omitted = send the generated message. */
  message?: string;
  /**
   * The language previewed. Sent so the message that goes out is the one the
   * recruiter approved — without it the server would re-derive from the role's
   * tags and could send the other language's DMs under an edited channel post.
   */
  language?: 'de' | 'en';
}): Promise<{ ok: boolean; sent: string[]; failed: string[] }> {
  return tracked(
    'ta_board_ask',
    async () => {
      const res = await authenticatedFetch(`${BASE}/ask-panelists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return res.json() as Promise<{ ok: boolean; sent: string[]; failed: string[] }>;
    },
    // DMs vs one channel message is a real behavioural split worth seeing.
    // `.type`, not the object: AskDestination is `{type:'dms'} | {type:'channel', channel}`, and
    // interpolating it whole records "[object Object]". The channel NAME is deliberately not sent.
    { destination: payload.destination?.type ?? 'dms', slot_count: payload.slots.length }
  );
}

/**
 * An upcoming interview an interviewer has DECLINED or not answered.
 *
 * Its own call rather than part of the board payload: it costs one Google read
 * per upcoming interview, and it is only looked at when the Overdue tab is open.
 */
export interface InviteResponseRow {
  opportunityId: string | null;
  candidateName: string | null;
  stageName: string | null;
  start: string | null;
  /** The event could not be read — reported, because silence is a false all-clear. */
  unreadable: boolean;
  declined: { email: string; name: string | null; response: string }[];
  /** Not answered, or "maybe" — neither is cover for an interview. */
  awaiting: { email: string; name: string | null; response: string }[];
}

export async function fetchInviteResponses(
  boardEmail: string
): Promise<{ rows: InviteResponseRow[]; checked: number; capped: boolean; error?: string }> {
  const res = await authenticatedFetch(
    `${BASE}/invite-responses?email=${encodeURIComponent(boardEmail)}`
  );
  if (!res.ok) {
    // A failure must not read as "everyone accepted".
    return { rows: [], checked: 0, capped: false, error: 'Could not read the invite responses.' };
  }
  return res.json();
}
